import type { OptimizedMessage } from '../services/api.ts';
import type { MessageDeliveryError, TemplateMessageSnapshot } from '../types/templateMessage.ts';

export interface MessageDeliveryState {
  status?: OptimizedMessage['status'];
  ack?: number | null;
  deliveryError?: MessageDeliveryError | null;
  deliveryStatusAt?: string | null;
  templateSnapshot?: TemplateMessageSnapshot | null;
}

const deliveryStatusFromAck = (ack: MessageDeliveryState['ack']): OptimizedMessage['status'] =>
  ({ '-1': 'failed', '0': 'sending', '1': 'sent', '2': 'delivered', '3': 'read', '4': 'read' } as const)[
    String(ack) as '-1' | '0' | '1' | '2' | '3' | '4'
  ];

const deliveryStatus = (state: MessageDeliveryState): OptimizedMessage['status'] =>
  state.deliveryError ? 'failed' : state.status || deliveryStatusFromAck(state.ack);

const deliveryTime = (state: MessageDeliveryState): number | null => {
  const value = state.deliveryStatusAt || state.deliveryError?.timestamp;
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/.test(value)) return null;
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : null;
};

const copyPresentation = <T,>(value: T): T => {
  if (Array.isArray(value)) return value.map(copyPresentation) as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, copyPresentation(item)])) as T;
  }
  return value;
};

/** Concilia recibos sem deixar um ACK ou cache antigo apagar falha confirmada. */
export const mergeMessageDelivery = (
  existing: MessageDeliveryState,
  incoming: MessageDeliveryState,
): MessageDeliveryState => {
  const previousStatus = deliveryStatus(existing);
  const nextStatus = deliveryStatus(incoming);
  const previousTime = deliveryTime(existing);
  const nextTime = deliveryTime(incoming);
  const later = nextTime !== null && previousTime !== null && nextTime > previousTime;
  const notEarlier = nextTime !== null && previousTime !== null && nextTime >= previousTime;
  const previousOfficialFailure = existing.deliveryError?.provider === 'official_whatsapp'
    || typeof existing.deliveryStatusAt === 'string';
  const rank = { sending: 0, sent: 1, delivered: 2, read: 3 };
  let selected = existing;

  if (!previousStatus) selected = incoming;
  else if (nextStatus === 'failed') {
    if (previousStatus === 'failed') {
      if (later || (previousTime === null && nextTime !== null)) selected = incoming;
    } else if (previousStatus === 'sending' || previousStatus === 'sent'
      || previousTime === null || nextTime === null || later) selected = incoming;
  } else if (previousStatus === 'failed') {
    if ((nextStatus === 'delivered' || nextStatus === 'read') && (
      previousOfficialFailure ? incoming.deliveryError === null && notEarlier : incoming.deliveryError == null
    )) selected = incoming;
  } else if (nextStatus && rank[nextStatus] >= rank[previousStatus]
    && !(nextTime !== null && previousTime !== null && nextTime < previousTime)) selected = incoming;

  const status = deliveryStatus(selected);
  const ackForStatus = { failed: -1, sending: 0, sent: 1, delivered: 2, read: 3 };
  const ack = status && deliveryStatusFromAck(selected.ack) !== status ? ackForStatus[status] : selected.ack;
  const snapshot = !existing.templateSnapshot ? incoming.templateSnapshot
    : incoming.templateSnapshot && nextTime !== null && (previousTime === null || later)
      ? incoming.templateSnapshot : existing.templateSnapshot;
  const legacyRecovery = selected === incoming && previousStatus === 'failed' && !previousOfficialFailure
    && (status === 'delivered' || status === 'read');
  const selectedError = legacyRecovery ? null : selected.deliveryError !== undefined ? selected.deliveryError
    : status === 'failed' ? existing.deliveryError || incoming.deliveryError : existing.deliveryError;
  // Um delivered posterior comprova entrega mais recente, sem rebaixar read.
  const advanceSuccessTime = (status === 'delivered' || status === 'read')
    && (nextStatus === 'delivered' || nextStatus === 'read')
    && incoming.deliveryError === null && nextTime !== null
    && (previousTime === null || notEarlier);
  return {
    status,
    ack,
    deliveryError: copyPresentation(selectedError),
    deliveryStatusAt: advanceSuccessTime ? incoming.deliveryStatusAt || null
      : selected.deliveryStatusAt || selected.deliveryError?.timestamp || null,
    templateSnapshot: copyPresentation(snapshot),
  };
};

/** Concilia identidade e entrega sem depender de autoria ou aprendizagem. */
export const reconcileMessageDelivery = <T extends MessageDeliveryState & {
  id: string;
  databaseMessageId?: number | null;
  providerMessageId?: string | null;
}>(
  existing: T,
  incoming: T,
): T => {
  const existingHasDbId = Number.isInteger(existing.databaseMessageId) && Number(existing.databaseMessageId) > 0;
  const incomingHasDbId = Number.isInteger(incoming.databaseMessageId) && Number(incoming.databaseMessageId) > 0;
  const base = incomingHasDbId || !existingHasDbId ? incoming : existing;
  return {
    ...base,
    ...mergeMessageDelivery(existing, incoming),
    id: incomingHasDbId ? incoming.id : existingHasDbId ? existing.id : base.id,
    databaseMessageId: incoming.databaseMessageId || existing.databaseMessageId,
    providerMessageId: incoming.providerMessageId || existing.providerMessageId,
  };
};
